/*
PROV: COHINT001.BF.3XM7J2EW4E
REQ: SYS-ARCH-15
WHY: Next.js page module.
*/

import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Button } from "@cohorta/ui";
import { sitePath } from "../lib/sitePrefix";
import { getJson } from "../lib/api";

function applyTheme(isDark) {
  document.documentElement.classList.toggle("dark", isDark);
  try { localStorage.setItem("cohorta_theme", isDark ? "dark" : "light"); } catch { /* ignore */ }
}

export default function AdminLandingPage() {
  const router = useRouter();
  const [checking, setChecking] = useState(true);
  const [darkMode, setDarkMode] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("cohorta_theme");
    const prefersDark = stored ? stored === "dark" : document.documentElement.classList.contains("dark");
    setDarkMode(prefersDark);
    setMounted(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const data = await getJson("/api/v1/admin/auth_check");
        if (cancelled) return;
        if (data?.authorized && data?.auth_required) {
          router.replace(sitePath(data?.account?.is_superuser ? "/superuser-hub" : "/groups"));
          return;
        }
      } catch {
        // ignore
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [router]);

  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  return (
    <div className="min-h-screen font-sans bg-brand-50 dark:bg-brand-950 text-brand-900 dark:text-brand-100">
      <Head>
        <title>Group Owners Portal</title>
        <meta name="description" content="Group Owners Portal" />
      </Head>
      <main className="mx-auto max-w-[980px] px-4 py-6 sm:px-6 sm:py-8">
        <section className="relative overflow-hidden rounded-2xl border border-brand-200 bg-white p-6 shadow-sm dark:border-brand-700 dark:bg-brand-900 sm:p-8">
          <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-brand-200/35 blur-3xl dark:bg-brand-700/30" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-brand-100/60 blur-3xl dark:bg-brand-800/40" />

          <div className="relative">
            <div className="mx-auto max-w-[620px] text-center">
              <div className="mb-5 flex items-center justify-center gap-3">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/logo.gif" alt="Cohorta" className="h-10 w-auto sm:h-12" />
                <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-600 dark:border-brand-600 dark:bg-brand-800 dark:text-brand-200">
                  Group Admin
                </span>
              </div>

              <h1 className="m-0 text-3xl font-extrabold leading-tight text-brand-900 dark:text-brand-100 sm:text-4xl">
                Group Owners Portal
              </h1>
              <p className="mx-auto mt-3 max-w-[58ch] text-sm leading-relaxed text-brand-600 dark:text-brand-300 sm:text-[15px]">
                Manage members, monitor invite code usage, and keep your group operations running smoothly from one place.
              </p>

              <div className="mt-5 flex justify-center gap-3 flex-wrap">
                <Button
                  type="button"
                  onClick={() => router.push(sitePath("/login"))}
                  className="min-w-[160px] sm:min-w-[180px]"
                >
                  Sign in
                </Button>
              </div>

              <div className="mt-4 text-xs text-brand-500 dark:text-brand-400">
                {checking ? "Checking session..." : "Sign in to continue to your dashboard."}
              </div>
            </div>
          </div>

          <div className="relative mt-6 flex justify-center">
            {mounted ? (
              <button
                type="button"
                onClick={() => setDarkMode((prev) => !prev)}
                className="border-0 bg-transparent p-0 text-[11px] text-brand-400 dark:text-brand-500 cursor-pointer hover:text-brand-600 dark:hover:text-brand-300 transition-colors"
                aria-label="Toggle theme"
              >
                {darkMode ? "Switch to light mode" : "Switch to dark mode"}
              </button>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
